import type { ComponentType } from "react";
import {
  FileText,
  FlaskConical,
  Image as ImageIcon,
  Lock,
  Plus,
  Target,
  Zap,
} from "lucide-react";
import { useSetPageHeader } from "@/lib/page-header";
import { CartaoComDestaque } from "@/components/shared/CartaoComDestaque";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { APPS_PRO_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";

type AppStatus = "available" | "beta" | "soon";

type AppDef = {
  id: string;
  name: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  /** Cor hex usada no ícone, borda superior e pills. */
  color: string;
  status: AppStatus;
  tags: string[];
};

const APPS: AppDef[] = [
  {
    id: "dicom-viewer",
    name: "DICOM Viewer Pro",
    description:
      "Visualizador profissional de tomografias com MPR, 3D Volume e ferramentas avançadas, otimizado para odontologia, com ferramentas de IA.",
    icon: ImageIcon,
    color: "#3b82f6",
    status: "soon",
    tags: ["MPR 3 Cortes", "Volume 3D", "Régua", "Medições 2D", "DICOM anonimizado"],
  },
  {
    id: "raiox-ia",
    name: "Raio X - IA",
    description:
      "Assistente de IA para radiografias. Identifica estruturas e patologias automaticamente em Cefalometrias, Panorâmicas e Periapicais.",
    icon: Plus,
    color: "#06b6d4",
    status: "soon",
    tags: ["Cefalometrias", "Panorâmicas", "Periapicais", "Detecção de patologias", "Análise de dentes"],
  },
  {
    id: "paciente-virtual",
    name: "Paciente Virtual",
    description:
      "Combine a tomografia (CBCT) com o escaneamento intraoral do paciente em um único modelo 3D. Ideal para planejamento cirúrgico e protético.",
    icon: Target,
    color: "#ef4444",
    status: "soon",
    tags: ["CBCT + IOS", "Alinhamento automático", "Precisão milimétrica", "Modelo 3D unificado", "Planejamento cirúrgico"],
  },
  {
    id: "radio-template",
    name: "Radiologia Template IA",
    description:
      "Templates inteligentes para laudos com preenchimento automático.",
    icon: FileText,
    color: "#ec4899",
    status: "soon",
    tags: ["Templates IA", "Preenchimento auto", "Biblioteca de achados"],
  },
];

const STATUS_META: Record<
  AppStatus,
  { label: string; icon: ComponentType<{ className?: string }>; color: string }
> = {
  available: { label: "Disponível", icon: Zap, color: "#22c55e" },
  beta: { label: "Beta", icon: FlaskConical, color: "#f59e0b" },
  soon: { label: "Em Breve", icon: Lock, color: "#71717a" },
};

export function AppsPro() {
  useTourDaPagina(APPS_PRO_TOUR_STEPS);
  useSetPageHeader(
    {
      title: "Apps Pro",
      subtitle: "Aplicativos profissionais com IA",
    },
    [],
  );

  return (
    <div className="mx-auto w-full max-w-[1600px]">
      <div data-tour="apps-pro-grid" className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {APPS.map((app) => (
          <AppCard key={app.id} app={app} />
        ))}
      </div>
    </div>
  );
}

function AppCard({ app }: { app: AppDef }) {
  const meta = STATUS_META[app.status];
  const StatusIcon = meta.icon;
  const visibleTags = app.tags.slice(0, 3);
  const extraTags = app.tags.length - visibleTags.length;
  const estiloDaTag = {
    backgroundColor: `${app.color}14`,
    color: app.color,
    boxShadow: `inset 0 0 0 1px ${app.color}26`,
  };

  return (
    <CartaoComDestaque
      cor={app.color}
      icone={app.icon}
      titulo={app.name}
      descricao={app.description}
      apagado={app.status === "soon"}
      className="min-h-[220px]"
      canto={
        <span
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset"
          style={{
            backgroundColor: `${meta.color}1f`,
            color: meta.color,
            boxShadow: `inset 0 0 0 1px ${meta.color}40`,
          }}
        >
          <StatusIcon className="h-3 w-3" />
          {meta.label}
        </span>
      }
    >
      <div className="relative mt-4 flex flex-wrap gap-1.5">
        {visibleTags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center rounded-md px-1.5 py-[3px] text-[10px] font-medium ring-1 ring-inset"
            style={estiloDaTag}
          >
            {tag}
          </span>
        ))}
        {extraTags > 0 && (
          <span className="inline-flex items-center rounded-md px-1.5 py-[3px] text-[10px] font-semibold" style={estiloDaTag}>
            +{extraTags}
          </span>
        )}
      </div>
    </CartaoComDestaque>
  );
}
